import { useEffect, useState, type ReactNode } from 'react';
import { useGameStore, EventBus } from '@campus/core';
import { useDeviceEnumeration, deviceOptions, SUPPORTS_AUDIO_OUTPUT_SELECTION, type DeviceKind } from '../hooks/useDeviceEnumeration.js';
import { useIsMobile } from '../hooks/useIsMobile.js';
import { Modal, Switch, Select, Tabs, Icon, cx, type IconName, type ThemePreference } from '../ds/index.js';
import { VideoBackgroundPicker } from '../components/VideoBackgroundPicker.js';

const ZOOM_OPTIONS = [
  { value: '0.5', label: '50 %' },
  { value: '0.75', label: '75 %' },
  { value: '1', label: '100 %' },
  { value: '1.5', label: '150 %' },
  { value: '2', label: '200 %' },
];

// « Système » n'est pas un thème de plus : il suit le réglage clair/sombre de
// l'ordinateur (voir applyTheme, ds/theme.ts).
const THEMES: Array<{ id: ThemePreference; label: string }> = [
  { id: 'dark', label: 'Sombre' },
  { id: 'light', label: 'Clair' },
  { id: 'campus', label: 'Campus' },
  { id: 'system', label: 'Système' },
];

const k = (key: string) => <kbd className="pl-key">{key}</kbd>;

/**
 * Raccourcis (Réglages › Raccourcis clavier). À tenir à jour : toute
 * modification ou nouvelle fonction qui ajoute, change ou retire un raccourci
 * clavier ou souris met aussi à jour cette liste (règle de CLAUDE.md).
 */
const SHORTCUTS: Array<{ group: string; items: Array<{ action: string; keys: ReactNode }> }> = [
  {
    group: 'Se déplacer',
    items: [
      { action: 'Marcher', keys: <>{k('Z')}{k('Q')}{k('S')}{k('D')}ou flèches</> },
      { action: 'Courir', keys: <>{k('Maj')}en marchant</> },
      { action: 'Aller à un endroit', keys: <>double-clic sur la carte</> },
      { action: 'Déplacer la vue', keys: <>cliquer et glisser</> },
      { action: 'Zoomer, dézoomer', keys: <>{k('+')}{k('−')}molette ou pincement</> },
    ],
  },
  {
    group: 'Agir',
    items: [
      { action: 'Ouvrir une porte, s\'asseoir, se lever, prendre une trottinette, ouvrir un tableau blanc', keys: <>{k('E')}ou{k('Espace')}</> },
      { action: 'Ouvrir un lien ou une app', keys: k('F') },
      { action: 'Choisir un étage dans l\'ascenseur', keys: <>chiffre de l'étage, {k('Échap')} pour fermer</> },
      { action: 'Faire coucou à quelqu\'un', keys: <>double-clic sur son avatar</> },
    ],
  },
  {
    group: 'Discuter',
    items: [
      { action: 'Ouvrir ou fermer la messagerie', keys: k('Entrée') },
      { action: 'Envoyer un message (retour à la ligne avec Maj)', keys: k('Entrée') },
      { action: 'Modifier son dernier message (saisie vide)', keys: k('↑') },
      { action: 'Annuler la modification d\'un message', keys: k('Échap') },
      { action: 'Réagir d\'un pouce levé à un message', keys: <>double-clic sur le message</> },
      { action: 'Annuler la réponse à un message', keys: k('Échap') },
      { action: 'Discussion de la bulle (messages de la réunion en salle de réunion)', keys: k('T') },
      { action: 'Agrandir une vidéo', keys: <>double-clic sur la vignette</> },
      { action: 'Zoomer sur un écran partagé agrandi (glisser pour se déplacer, double-clic pour revenir)', keys: <>molette ou pincement</> },
      { action: 'Réduire la vue réunion', keys: k('Échap') },
      { action: 'Quitter le plein écran d\'un écran partagé', keys: k('Échap') },
    ],
  },
  {
    group: 'Affichage',
    items: [
      { action: 'Lumière d\'ambiance (allumer, éteindre)', keys: k('L') },
      { action: 'Fermer une fenêtre', keys: k('Échap') },
    ],
  },
  {
    group: 'Personnaliser son bureau',
    items: [
      { action: 'Reposer l\'objet tenu à sa place', keys: k('Échap') },
      { action: 'Retirer l\'objet tenu', keys: <>{k('Suppr')}ou{k('⌫')}</> },
    ],
  },
  {
    group: 'Éditeur de carte (admins)',
    items: [
      { action: 'Onglets Sélection, Structure, Mobilier, Organisation', keys: <>{k('1')}{k('2')}{k('3')}{k('4')}</> },
      { action: 'Sols, Murs, Portes', keys: <>{k('S')}{k('M')}{k('P')}</> },
      { action: 'Zones, Textes, Emplacements', keys: <>{k('Z')}{k('T')}{k('E')}</> },
      { action: 'Vue suivante de l\'objet (précédente avec Maj)', keys: k('R') },
    ],
  },
];

type Section = 'audio' | 'notifications' | 'display' | 'keys';

const SECTIONS: Array<{ id: Section; label: string; icon: IconName }> = [
  { id: 'audio', label: 'Audio et vidéo', icon: 'mic' },
  { id: 'notifications', label: 'Notifications', icon: 'bell' },
  { id: 'display', label: 'Affichage', icon: 'monitor' },
  { id: 'keys', label: 'Raccourcis clavier', icon: 'keyboard' },
];

/**
 * SettingsModal — réglages personnels (localStorage, voir gameStore.settings).
 *
 * Ordinateur : navigation par section à gauche. Mobile : plein écran, sections
 * à la suite, sans les raccourcis clavier.
 * Énumération des périphériques : useDeviceEnumeration.ts (partagé avec
 * LobbyPage) — pas de demande de permission forcée à l'ouverture.
 */
export function SettingsModal({ onClose }: { onClose: () => void }) {
  const settings = useGameStore((s) => s.settings);
  const updateSettings = useGameStore((s) => s.updateSettings);
  const setZoomLevel = useGameStore((s) => s.setZoomLevel);
  const isMobile = useIsMobile();
  const [section, setSection] = useState<Section>('audio');

  const devices = useDeviceEnumeration();

  // Libère le clavier de Phaser pendant que la fenêtre est ouverte
  useEffect(() => {
    try { EventBus.emit('ui:modal-opened', {}); } catch { /* */ }
    return () => { try { EventBus.emit('ui:modal-closed', {}); } catch { /* */ } };
  }, []);

  function handleZoomChange(value: number) {
    updateSettings({ defaultZoom: value });
    setZoomLevel(value);
    EventBus.emit('ui:zoom-change', { level: value });
  }

  function handleDeviceChange(kind: DeviceKind, storeKey: 'selectedMicId' | 'selectedCameraId' | 'selectedSpeakerId', deviceId: string) {
    updateSettings({ [storeKey]: deviceId });
    EventBus.emit('ui:device-changed', { kind, deviceId });
  }

  function device(label: string, kind: DeviceKind, storeKey: 'selectedMicId' | 'selectedCameraId' | 'selectedSpeakerId', fallback: string) {
    const select = (
      <Select
        {...(isMobile ? { label } : { 'aria-label': label })}
        value={settings[storeKey]}
        options={deviceOptions(devices, kind, fallback)}
        onChange={(e) => handleDeviceChange(kind, storeKey, e.target.value)}
      />
    );
    if (isMobile) return <div style={{ marginTop: 8 }}>{select}</div>;
    return <div className="pl-row"><span className="pl-row-label">{label}</span><div style={{ width: 220 }}>{select}</div></div>;
  }

  const audio = (
    <>
      <div className="pl-row">
        <Switch label="Micro activé en entrant" hint="Sinon, tu entres micro coupé." checked={settings.audioEnabled} onChange={(v) => updateSettings({ audioEnabled: v })} />
      </div>
      <div className="pl-row">
        <Switch label="Caméra activée en entrant" checked={settings.videoEnabled} onChange={(v) => updateSettings({ videoEnabled: v })} />
      </div>
      {device('Microphone', 'audioinput', 'selectedMicId', 'Microphone')}
      {device('Caméra', 'videoinput', 'selectedCameraId', 'Caméra')}
      <VideoBackgroundPicker />
      {SUPPORTS_AUDIO_OUTPUT_SELECTION && device('Haut-parleur', 'audiooutput', 'selectedSpeakerId', 'Haut-parleur')}
    </>
  );

  const notifications = (
    <>
      <div className="pl-row">
        <Switch label="Sons" hint="Quand quelqu'un toque ou t'écrit." checked={settings.soundsEnabled} onChange={(v) => updateSettings({ soundsEnabled: v })} />
      </div>
      <div className="pl-row">
        <Switch label="Notifications à l'écran" hint="Petits messages en bas à droite." checked={settings.toastsEnabled} onChange={(v) => updateSettings({ toastsEnabled: v })} />
      </div>
    </>
  );

  const theme = (
    <Tabs aria-label="Thème" items={THEMES} value={settings.theme} onChange={(t) => updateSettings({ theme: t })} />
  );

  const display = (
    <>
      <div className="pl-row"><span className="pl-row-label">Thème</span>{theme}</div>
      <div className="pl-row">
        <Switch label="Réduire les animations" checked={settings.reduceMotion} onChange={(v) => updateSettings({ reduceMotion: v })} />
      </div>
      <div className="pl-row">
        <Switch label="Lumière d'ambiance" hint="Lumière douce et ombres de nuages sur le campus. Touche L pour comparer." checked={settings.ambientLight} onChange={(v) => updateSettings({ ambientLight: v })} />
      </div>
      <div className="pl-row">
        <span className="pl-row-label">Zoom par défaut</span>
        <div style={{ width: 120 }}>
          <Select aria-label="Zoom par défaut" value={String(settings.defaultZoom)} options={ZOOM_OPTIONS} onChange={(e) => handleZoomChange(parseFloat(e.target.value))} />
        </div>
      </div>
    </>
  );

  const keys = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <p className="pl-help" style={{ margin: 0 }}>Les lettres sont celles d&apos;un clavier AZERTY : sur un clavier QWERTY, on marche avec W, A, S, D.</p>
      {SHORTCUTS.map((g) => (
        <div key={g.group}>
          <p className="pl-group-title">{g.group}</p>
          {g.items.map((s) => (
            <div key={s.action} className="pl-row"><span className="pl-row-label">{s.action}</span><span className="pl-keys">{s.keys}</span></div>
          ))}
        </div>
      ))}
    </div>
  );

  if (isMobile) {
    return (
      <Modal title="Réglages" onClose={onClose}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, color: 'var(--color-text-primary)' }}>
          <div><p className="pl-group-title">Audio et vidéo</p>{audio}</div>
          <div><p className="pl-group-title">Notifications</p>{notifications}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}><p className="pl-group-title">Thème</p>{theme}</div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Réglages" size="lg" onClose={onClose}>
      <div className="pl-sections">
        <nav className="pl-sections-nav" aria-label="Sections des réglages">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              className={cx('pl-snav', section === s.id && 'pl-snav-on')}
              aria-current={section === s.id ? 'true' : undefined}
              onClick={() => setSection(s.id)}
            >
              <Icon name={s.icon} size={16} />{s.label}
            </button>
          ))}
        </nav>
        <div className="pl-sections-body">
          {section === 'audio' && audio}
          {section === 'notifications' && notifications}
          {section === 'display' && display}
          {section === 'keys' && keys}
        </div>
      </div>
    </Modal>
  );
}
